import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import PostCard from "../components/PostCard";
import CommentsDrawer from "../components/CommentsDrawer";
import FeaturedRoomsStrip from "../components/FeaturedRoomsStrip";
import TrendingVoiceDropsStrip from "../components/TrendingVoiceDropsStrip";
import StreakClaim from "../components/StreakClaim";
import { Link } from "react-router-dom";
import { Broadcast, MagnifyingGlass } from "@phosphor-icons/react";

const FALLBACKS = [
  "https://images.unsplash.com/photo-1767801829041-f5243a34525d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1080",
  "https://images.unsplash.com/photo-1621826269707-5b27afe47c79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1080",
  "https://images.unsplash.com/photo-1734448824631-458e1b9a8b5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1080",
];

// Module-level stale-while-revalidate cache keyed by tab: instant paint on
// tab switch / back-navigation, silent refresh in the background.
let feedCache = {};

const FEED_TABS = [
  { k: "foryou", label: "For You", endpoint: "/posts/feed" },
  { k: "dance", label: "Dance", endpoint: "/posts/category/dance" },
  { k: "music", label: "Music", endpoint: "/posts/category/music" },
  { k: "diy", label: "DIY", endpoint: "/posts/category/diy" },
  { k: "community", label: "Community", endpoint: "/posts/category/community" },
];

export default function Feed() {
  const [tab, setTab] = useState("foryou");
  const [posts, setPosts] = useState(feedCache["foryou"] || null);
  const [active, setActive] = useState(null);
  const [nudge, setNudge] = useState(null);

  const load = useCallback(async () => {
    const ep = FEED_TABS.find((t) => t.k === tab)?.endpoint || "/posts/feed";
    try {
      const { data } = await api.get(ep);
      feedCache[tab] = data;
      setPosts(data);
    } catch (err) {
      console.error("feed load failed", err);
      if (!feedCache[tab]) setPosts([]);
    }
  }, [tab]);

  useEffect(() => { setPosts(feedCache[tab] || null); load(); }, [tab, load]);
  useEffect(() => {
    api.get("/media/recap/nudge").then((r) => { if (r.data?.show) setNudge(r.data); }).catch(() => {});
  }, []);
  const dismissNudge = () => { setNudge(null); api.post("/media/recap/nudge/dismiss").catch(() => {}); };

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-ink">
      {/* Top header */}
      <header className="absolute top-0 inset-x-0 z-30 px-5 pt-5">
        <div className="flex items-center justify-between">
          <Link to="/live" data-testid="header-go-live" className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.2em] uppercase hover:bg-papaya-hover transition-colors animate-pulse-glow">
            <Broadcast size={14} weight="fill" />
            Go Live
          </Link>
          <div className="flex flex-col items-center">
            <h1 className="text-cream font-black text-lg tracking-tighter">Giffwurldaunce</h1>
            <div className="jam-stripe h-[3px] w-16 rounded-full mt-1" aria-hidden />
          </div>
          <Link to="/explore" data-testid="header-search" className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream hover:text-lignum transition-colors">
            <MagnifyingGlass size={18} weight="bold" />
          </Link>
        </div>
        <div className="mt-3 flex items-center gap-5 overflow-x-auto" data-testid="feed-tabs">
          {FEED_TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`feed-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className="relative flex-shrink-0 pb-1"
            >
              <span className={`text-sm font-black tracking-wide transition-colors ${tab === t.k ? "text-cream" : "text-cream/50"}`}>
                {t.label}
              </span>
              {tab === t.k && <span className="absolute -bottom-0.5 left-0 right-0 h-[3px] rounded-full bg-papaya" />}
            </button>
          ))}
        </div>
      </header>

      {/* Featured Rooms discovery strip — surfaces top active public/paid rooms
          on the Home feed. Renders as an overlay above the vertical snap feed. */}
      <FeaturedRoomsStrip />
      <TrendingVoiceDropsStrip />
      <StreakClaim />

      {nudge && (
        <div className="absolute top-16 inset-x-0 z-30 px-4" data-testid="recap-nudge">
          <div className="crystal-glass rounded-2xl p-3 flex items-center gap-3 border border-papaya/30 animate-fade-up">
            <div className="text-2xl">🎬</div>
            <div className="flex-1 min-w-0">
              <p className="text-cream text-sm font-black">Your {nudge.month} Recap is ready</p>
              <p className="text-cream/60 text-xs truncate">Turn this month's posts into a montage — one tap.</p>
            </div>
            <Link to="/media-studio?tab=recap" data-testid="recap-nudge-build" className="flex-shrink-0 px-4 py-2 rounded-full bg-papaya text-ink text-[11px] font-black uppercase tracking-wide">Build</Link>
            <button onClick={dismissNudge} data-testid="recap-nudge-dismiss" className="flex-shrink-0 text-cream/50 hover:text-cream px-1">✕</button>
          </div>
        </div>
      )}

      {/* Feed */}
      {posts === null ? (
        <div className="h-full flex items-center justify-center text-cream/40 text-xs tracking-[0.3em] uppercase">Loading feed…</div>
      ) : posts.length === 0 ? (
        <EmptyFeed />
      ) : (
        <div className="feed-snap h-[100dvh] overflow-y-scroll">
          {posts.map((p, i) => (
            <PostCard
              key={p.id}
              post={p}
              fallbackImage={FALLBACKS[i % FALLBACKS.length]}
              onOpenComments={setActive}
              onUpdate={load}
            />
          ))}
        </div>
      )}
      {active && <CommentsDrawer post={active} onClose={() => setActive(null)} onAdded={load} />}
    </div>
  );
}

function EmptyFeed() {
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 text-center">
      <div className="text-7xl mb-4">🌴</div>
      <h2 className="text-cream font-black text-3xl tracking-tighter">Welcome to Giffwurldaunce</h2>
      <p className="text-cream/60 mt-3 max-w-sm text-sm leading-relaxed">
        The dance floor is empty. Be the first to post and set the tempo for dancers around the world.
      </p>
      <Link to="/create" data-testid="empty-create-cta" className="mt-6 px-6 py-3 rounded-full bg-papaya text-ink font-black text-sm tracking-[0.2em] uppercase hover:bg-papaya-hover transition-colors">
        Drop the First Post
      </Link>
    </div>
  );
}
